<template>
    <v-container fluid class="mt-12">

        <v-tabs v-model="tab" color="#3988AA" class="mb-6" background-color="transparent">
            <v-tab>Payments</v-tab>
            <v-tab>Low Profit</v-tab>
        </v-tabs>

        <LowProfitQuotes v-if="tab === 1" />

        <div v-show="tab === 0">
        <!-- Date Range Controls -->
        <v-row align="center" class="mb-4" no-gutters>
            <v-col cols="auto" class="mr-3">
                <span class="label-text-color text-caption font-weight-bold mr-2">QUICK SELECT</span>
                <v-btn small :color="quick_select === 'last_7_days' ? '#3988AA' : ''"
                    :dark="quick_select === 'last_7_days'" outlined class="mr-2"
                    @click="selectQuickRange('last_7_days')">
                    Last 7 Days
                </v-btn>
                <v-btn small :color="quick_select === 'prev_7_days' ? '#3988AA' : ''"
                    :dark="quick_select === 'prev_7_days'" outlined @click="selectQuickRange('prev_7_days')">
                    Previous 7 Days
                </v-btn>
            </v-col>

            <v-col cols="auto" class="mr-2">
                <v-menu v-model="from_menu" :close-on-content-click="false" transition="scale-transition" offset-y
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="date_from" label="From" readonly outlined dense hide-details
                            prepend-inner-icon="mdi-calendar" style="max-width:175px;" v-bind="attrs" v-on="on">
                        </v-text-field>
                    </template>
                    <v-date-picker v-model="date_from" no-title :max="date_to"
                        @input="onDateChange('from')"></v-date-picker>
                </v-menu>
            </v-col>

            <v-col cols="auto" class="mr-3">
                <v-menu v-model="to_menu" :close-on-content-click="false" transition="scale-transition" offset-y
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="date_to" label="To" readonly outlined dense hide-details
                            prepend-inner-icon="mdi-calendar" style="max-width:175px;" v-bind="attrs" v-on="on">
                        </v-text-field>
                    </template>
                    <v-date-picker v-model="date_to" no-title :min="date_from"
                        @input="onDateChange('to')"></v-date-picker>
                </v-menu>
            </v-col>

            <v-col cols="auto">
                <v-progress-circular v-if="loading" indeterminate size="24" color="#3988AA"></v-progress-circular>
            </v-col>

            <v-spacer></v-spacer>

            <v-col cols="auto" class="mr-2">
                <v-btn small color="#679F54" dark :loading="exporting" @click="downloadCsv">
                    <v-icon small class="mr-1">mdi-download</v-icon>
                    Export CSV
                </v-btn>
            </v-col>

            <v-col cols="auto">
                <v-btn small color="#3988AA" dark :loading="exporting_accounts" @click="downloadAccountsCsv">
                    <v-icon small class="mr-1">mdi-download</v-icon>
                    Export Accounts CSV
                </v-btn>
            </v-col>
        </v-row>

        <!-- Income Chart -->
        <v-row>
            <v-col cols="12">
                <v-card outlined class="card-format pa-4">
                    <div class="chart-title">Daily Income</div>
                    <div class="chart-subtitle mb-4">Deposits &amp; full payments — split by organiser and invitee</div>
                    <apexchart v-if="!loading && chart_data" height="360" :options="income_chart_options"
                        :series="income_series">
                    </apexchart>
                    <div v-if="!loading && !chart_data" class="text-center label-text-color pa-8">No data available for
                        the
                        selected period.</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Contribution Chart -->
        <v-row class="mt-4">
            <v-col cols="12">
                <v-card outlined class="card-format pa-4">
                    <div class="chart-title">Daily Contributions</div>
                    <div class="chart-subtitle mb-4">Contribution payments only</div>
                    <apexchart v-if="!loading && chart_data" height="360" :options="contribution_chart_options"
                        :series="contribution_series">
                    </apexchart>
                    <div v-if="!loading && !chart_data" class="text-center label-text-color pa-8">No data available for
                        the
                        selected period.</div>
                </v-card>
            </v-col>
        </v-row>
        </div>

    </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import LowProfitQuotes from './LowProfitQuotes.vue'

function toDateStr(d) {
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function escapeCsvValue(val) {
    const str = val == null ? '' : String(val)
    return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str
}

function downloadCsvFile(csvLines, filename) {
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

function getLast7DaysRange(offsetWeeks) {
    const today = new Date()
    const to = new Date(today)
    to.setDate(today.getDate() - offsetWeeks * 7)
    const from = new Date(to)
    from.setDate(to.getDate() - 6)
    return { from: toDateStr(from), to: toDateStr(to) }
}

export default {
    components: {
        apexchart: VueApexCharts,
        LowProfitQuotes,
    },
    data() {
        const last7 = getLast7DaysRange(0)
        return {
            // ?tab=low-profit opens the Low Profit report directly.
            tab: this.$route.query.tab === 'low-profit' ? 1 : 0,
            loading: false,
            exporting: false,
            exporting_accounts: false,
            quick_select: 'last_7_days',
            date_from: last7.from,
            date_to: last7.to,
            from_menu: false,
            to_menu: false,
            chart_data: null,
        }
    },
    created() {
        this.setPageName('Reports')
    },
    async mounted() {
        await this.fetchChartData()
    },
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        income_series() {
            const d = this.chart_data?.income
            if (!d) return []
            return [
                { name: 'Organiser Amount', type: 'bar', data: d.organiser_amounts },
                { name: 'Invitee Amount', type: 'bar', data: d.invitee_amounts },
                { name: 'Organiser Payments', type: 'line', data: d.organiser_counts },
                { name: 'Invitee Payments', type: 'line', data: d.invitee_counts },
            ]
        },
        contribution_series() {
            const d = this.chart_data?.contributions
            if (!d) return []
            return [
                { name: 'Contribution Amount', type: 'bar', data: d.amounts },
                { name: 'Payments', type: 'line', data: d.counts },
            ]
        },
        income_chart_options() {
            return {
                chart: { type: 'bar', toolbar: { show: true } },
                plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
                dataLabels: { enabled: false },
                stroke: { width: [0, 0, 3, 3], curve: 'smooth' },
                markers: { size: [0, 0, 5, 5] },
                xaxis: {
                    categories: this.chart_data?.dates ?? [],
                    labels: {
                        rotate: -45,
                        rotateAlways: (this.chart_data?.dates ?? []).length > 10,
                        style: { fontSize: '11px' },
                    },
                },
                yaxis: [
                    {
                        seriesName: 'Organiser Amount',
                        title: { text: 'Amount' },
                        labels: { formatter: v => Number(v).toFixed(2) },
                        min: 0,
                    },
                    {
                        seriesName: 'Organiser Amount',
                        show: false,
                    },
                    {
                        seriesName: 'Organiser Payments',
                        opposite: true,
                        title: { text: 'No. of Payments' },
                        labels: { formatter: v => Math.round(v) },
                        min: 0,
                        tickAmount: 5,
                    },
                    {
                        seriesName: 'Organiser Payments',
                        opposite: true,
                        show: false,
                    },
                ],
                colors: ['#3988AA', '#25BDEE', '#AA3939', '#E85252'],
                fill: { opacity: [0.9, 0.9, 1, 1] },
                legend: { position: 'top', horizontalAlign: 'left' },
                tooltip: { shared: true, intersect: false },
                grid: { borderColor: '#f1f1f1' },
            }
        },
        contribution_chart_options() {
            return {
                chart: { type: 'bar', toolbar: { show: true } },
                plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
                dataLabels: { enabled: false },
                stroke: { width: [0, 3], curve: 'smooth' },
                markers: { size: [0, 5] },
                xaxis: {
                    categories: this.chart_data?.dates ?? [],
                    labels: {
                        rotate: -45,
                        rotateAlways: (this.chart_data?.dates ?? []).length > 10,
                        style: { fontSize: '11px' },
                    },
                },
                yaxis: [
                    {
                        seriesName: 'Contribution Amount',
                        title: { text: 'Amount' },
                        labels: { formatter: v => Number(v).toFixed(2) },
                        min: 0,
                    },
                    {
                        seriesName: 'Contribution Amount',
                        opposite: true,
                        title: { text: 'No. of Payments' },
                        labels: { formatter: v => Math.round(v) },
                        min: 0,
                        tickAmount: 5,
                    },
                ],
                colors: ['#679F54', '#679F54'],
                fill: { opacity: [0.9, 1] },
                legend: { position: 'top', horizontalAlign: 'left' },
                tooltip: { shared: true, intersect: false },
                grid: { borderColor: '#f1f1f1' },
            }
        },
    },
    methods: {
        ...mapActions({
            setPageName: 'pageNameStore/setPageNameAction',
        }),
        selectQuickRange(which) {
            this.quick_select = which
            const range = getLast7DaysRange(which === 'prev_7_days' ? 1 : 0)
            this.date_from = range.from
            this.date_to = range.to
            this.fetchChartData()
        },
        onDateChange(field) {
            if (field === 'from') this.from_menu = false
            else this.to_menu = false
            this.quick_select = null
            this.fetchChartData()
        },
        async fetchChartData() {
            this.loading = true
            try {
                const { data } = await this.$axios.get(
                    `admin/payments/get_payment_chart_data/${this.get_selected_company.id}`,
                    { from: this.date_from, to: this.date_to }
                )
                if (data.response) {
                    this.chart_data = data.data
                }
            } catch (e) {
                console.error('Failed to fetch chart data', e)
            } finally {
                this.loading = false
            }
        },

        async downloadCsv() {
            this.exporting = true
            try {
                const { data } = await this.$axios.get(
                    `admin/payments/get_payment_export/${this.get_selected_company.id}`,
                    { from: this.date_from, to: this.date_to }
                )
                if (!data.response) return

                const { rows, currency } = data.data
                if (!rows.length) return

                const headers = [
                    'Payment ID',
                    'Payment Date',
                    'Gateway',
                    'Paid By',
                    'Event',
                    'Payment Type',
                    'Processing Fee',
                    'Currency',
                    'Amount',
                ]

                const csvLines = [headers.join(',')]
                for (const row of rows) {
                    csvLines.push([
                        row.payment_id,
                        row.payment_date,
                        escapeCsvValue(row.gateway),
                        escapeCsvValue(row.paid_by),
                        escapeCsvValue(row.event),
                        escapeCsvValue(row.payment_type),
                        row.is_charge,
                        currency,
                        row.amount,
                    ].join(','))
                }

                downloadCsvFile(csvLines, `payments_report_${this.date_from}_to_${this.date_to}.csv`)
            } catch (e) {
                console.error('Failed to export CSV', e)
            } finally {
                this.exporting = false
            }
        },

        async downloadAccountsCsv() {
            this.exporting_accounts = true
            try {
                const { data } = await this.$axios.get(
                    `admin/payments/get_accounts_export/${this.get_selected_company.id}`,
                    { from: this.date_from, to: this.date_to }
                )
                if (!data.response) return

                const { rows, currency } = data.data
                if (!rows.length) return

                const headers = [
                    'Payment ID',
                    'Payment Date',
                    'Gateway',
                    'Paid By',
                    'Event',
                    'Transaction Reference',
                    'Currency',
                    'Amount',
                ]

                const csvLines = [headers.join(',')]
                for (const row of rows) {
                    csvLines.push([
                        row.payment_id,
                        row.payment_date,
                        escapeCsvValue(row.gateway),
                        escapeCsvValue(row.paid_by),
                        escapeCsvValue(row.event),
                        escapeCsvValue(row.reference),
                        currency,
                        row.amount,
                    ].join(','))
                }

                downloadCsvFile(csvLines, `accounts_report_${this.date_from}_to_${this.date_to}.csv`)
            } catch (e) {
                console.error('Failed to export accounts CSV', e)
            } finally {
                this.exporting_accounts = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.card-format {
    border-radius: 15px !important;
}

.chart-title {
    font-family: 'Work Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #3C3C3C;
}

.chart-subtitle {
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    color: #7C7C7C;
}

.label-text-color {
    color: #7C7C7C;
}
</style>
